import React, { useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Drawer, IconButton, Tooltip } from '@mui/material';
import {
  ChevronLeft,
  ChevronRight,
  Logout,
  VpnKey,
  Receipt,
  AccountBalanceWallet,
  AttachMoney,
  SwapCalls,
  EventNote
} from '@mui/icons-material';
import treasuryLogo from '../assets/treasury-logo.png';
import NotificationBell from './NotificationBell';
import './SideNav.css';

const DRAWER_WIDTH_EXPANDED = 220;
const DRAWER_WIDTH_COLLAPSED = 64;

const LINKS = [
  { tab: 'transactions', path: '/front-office-blotter', label: 'Transaction Blotter', icon: Receipt },
  { tab: 'fixed_income_gsec', path: '/front-office-gsec-blotter', label: 'GSec Blotter', icon: AccountBalanceWallet },
  { tab: 'fixed_income_tbill', path: '/fixed-income-tbill', label: 'T-Bill Blotter', icon: AccountBalanceWallet },
  { tab: 'money_market', path: '/front-office-money-market-blotter', label: 'Money Market Blotter', icon: AttachMoney },
  { tab: 'fixed_deposit', path: '/back-office-final-fixed-deposit-blotter', label: 'Fixed Deposit Blotter', icon: EventNote },
  { tab: 'repo', path: '/repo-authorization-blotter', label: 'Repo Blotter', icon: SwapCalls },
  { tab: 'daily-deal-blotter', path: '/daily-deal-blotter', label: 'Daily Deal Blotter', icon: Receipt },
  { tab: 'daily-transaction-blotter', path: '/daily-transaction-blotter', label: 'Daily Transaction Blotter', icon: Receipt }
];

function AuthorizerSideNav() {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const allowed = user?.allowed_tabs || [];
  const items = LINKS.filter((link) => allowed.includes(link.tab));

  const handleLogout = () => {
    localStorage.removeItem('user');
    window.location.href = '/login';
  };

  const toggleSidebar = () => {
    setCollapsed((prev) => !prev);
  };

  useEffect(() => {
    document.documentElement.style.setProperty(
      '--sidebar-width',
      collapsed ? `${DRAWER_WIDTH_COLLAPSED}px` : `${DRAWER_WIDTH_EXPANDED}px`
    );
  }, [collapsed]);

  return (
    <Drawer
      variant="permanent"
      className="sn-drawer"
      sx={{
        '& .MuiDrawer-paper': {
          width: collapsed ? DRAWER_WIDTH_COLLAPSED : DRAWER_WIDTH_EXPANDED,
          boxSizing: 'border-box',
          overflowX: 'hidden',
          overflowY: 'hidden',
          transition: 'width 0.2s ease',
          display: 'flex',
          flexDirection: 'column',
          height: 'calc(100% - var(--status-bar-height, 36px))'
        }
      }}
    >
      <div className={`sn-header${collapsed ? ' sn-header--collapsed' : ' sn-header--expanded'}`}>
        {!collapsed && (
          <img src={treasuryLogo} alt="Sherwood Treasury" className="sn-logo" />
        )}
        <IconButton
          onClick={toggleSidebar}
          size="small"
          className="sn-toggle"
          aria-label={collapsed ? 'Expand menu' : 'Collapse menu'}
        >
          {collapsed ? <ChevronRight fontSize="small" /> : <ChevronLeft fontSize="small" />}
        </IconButton>
      </div>

      <div className="sn-scroll">
        <div className="sn-menu" style={{ padding: collapsed ? '0 8px' : '0 8px', gap: 6 }}>
          {items.map((item) => {
            const Icon = item.icon;
            const selected = location.pathname === item.path;
            const className = [
              'sn-item',
              collapsed ? 'sn-item--collapsed' : '',
              selected ? 'is-active' : ''
            ].filter(Boolean).join(' ');

            const link = (
              <Link to={item.path} className={className} aria-label={item.label}>
                <span className="sn-item-icon"><Icon /></span>
                {!collapsed && <span className="sn-item-label">{item.label}</span>}
              </Link>
            );

            return collapsed ? (
              <Tooltip key={item.path} title={item.label} placement="right">
                {link}
              </Tooltip>
            ) : (
              <React.Fragment key={item.path}>{link}</React.Fragment>
            );
          })}
        </div>
      </div>

      <div className={`sn-footer${collapsed ? ' sn-footer--collapsed' : ''}`}>
        <NotificationBell collapsed={collapsed} />
        <Tooltip title={collapsed ? 'Change Password' : ''} placement="right" disableHoverListener={!collapsed}>
          <Link
            to="/change-password"
            className={`sn-footer-btn sn-footer-btn--password${collapsed ? ' sn-footer-btn--icon' : ''}`}
          >
            <VpnKey sx={{ fontSize: 18 }} />
            {!collapsed && 'Change Password'}
          </Link>
        </Tooltip>
        <Tooltip title={collapsed ? 'Logout' : ''} placement="right" disableHoverListener={!collapsed}>
          <button
            type="button"
            onClick={handleLogout}
            className={`sn-footer-btn sn-footer-btn--logout${collapsed ? ' sn-footer-btn--icon' : ''}`}
          >
            <Logout sx={{ fontSize: 18 }} />
            {!collapsed && 'Logout'}
          </button>
        </Tooltip>
      </div>
    </Drawer>
  );
}

export default AuthorizerSideNav;
